// THE NUMBERS A CONTEXT-OVERFLOW REFUSAL STATES (#4240). `failure.ts` answers
// "did the request not fit" as a boolean for GRADING; this answers "by how much,
// against what window" so a caller can LEARN the real window
// (`recordOverflowLimits`, `@developerz/db`) and size a compact-and-retry from the
// provider's own figures rather than a table's guess.

import { z } from 'zod';
import { hasLlmContextOverflowMarker } from './failure';

/**
 * What a provider's overflow refusal said, in tokens. Each field is `null` when
 * the sentence did not state it — MiniMax's `(2013)` states none of them, and it
 * is still an overflow. `null` for the whole answer means "not an overflow".
 *
 * `inputTokens` is the PROMPT only, never a request total: openrouter's endpoint
 * phrasing ("you requested about N tokens") is prompt + completion, and reading
 * it as the prompt would overstate what a compaction has to shed.
 */
export type ContextOverflow = {
  readonly contextWindow: number | null;
  readonly inputTokens: number | null;
  readonly requestedOutput: number | null;
};

/**
 * One capture group each, first match wins per field. Every row names the
 * provider sentence it reads, and each one is a sentence a wire this platform
 * dials has served (the first two are verbatim prod rows).
 *
 * - openrouter z-ai/glm: "…maximum context length of 1048576 tokens. You requested
 *   a total of 1050374 tokens: 130306 tokens from the input messages and 920068
 *   tokens for the completion."
 * - openrouter endpoint / OpenAI: "maximum context length is 1310720 tokens"
 * - kimi: "Your request exceeded model token limit: 1048576 (requested: 1050183)"
 * - Anthropic: "prompt is too long: 208310 tokens > 200000 maximum"
 * - OpenAI: "However, your messages resulted in 130512 tokens"
 * - openrouter endpoint, with its breakdown (prod, 2026-09-24): "…you requested about
 *   1704606 tokens (760483 of text input, 405 of tool input, 943718 in the output)" —
 *   the TEXT input is the prompt; the tool schema's few hundred tokens are left out.
 */
const WINDOW_PATTERNS: readonly RegExp[] = [
  /maximum context length (?:is|of) ([\d,]+)/i,
  /exceeded model token limit:? ([\d,]+)/i,
  /prompt is too long:\s*[\d,]+ tokens? > ([\d,]+)/i,
];
const INPUT_PATTERNS: readonly RegExp[] = [
  /([\d,]+) tokens? from the input messages/i,
  /prompt is too long:\s*([\d,]+) tokens?/i,
  /your messages resulted in ([\d,]+) tokens?/i,
  /\(([\d,]+) in the messages/i,
  /\(([\d,]+) of text input/i,
];
const OUTPUT_PATTERNS: readonly RegExp[] = [
  /([\d,]+) tokens? for the completion/i,
  /([\d,]+) in the completion/i,
  /([\d,]+) in the output/i,
];

/**
 * The ERROR SHAPES text arrives in, walked to a fixed depth. The bound names its
 * failure: a `cause` chain can be cyclic (an SDK re-wrapping its own error), and
 * an unbounded walk over one never returns. Five is past every real wrapper
 * stack measured here (`RetryError` → `APICallError` → `cause`).
 */
const MAX_ERROR_DEPTH = 5;

function collectText(err: unknown, depth: number, seen: Set<unknown>, out: string[]): void {
  if (depth > MAX_ERROR_DEPTH || err === null || err === undefined) return;
  if (typeof err === 'string') {
    out.push(err);
    return;
  }
  if (typeof err !== 'object' || seen.has(err)) return;
  seen.add(err);
  const bag = err as Record<string, unknown>;
  for (const field of ['message', 'responseBody', 'body', 'stderr'] as const) {
    if (typeof bag[field] === 'string') out.push(bag[field]);
  }
  // `data` is the AI SDK's PARSED body; its `error.code` is where OpenAI's
  // machine-readable `context_length_exceeded` lives when no prose is kept.
  if (bag['data'] !== undefined && typeof bag['data'] === 'object') {
    out.push(JSON.stringify(bag['data']));
  }
  if (Array.isArray(bag['errors'])) {
    for (const inner of bag['errors']) collectText(inner, depth + 1, seen, out);
  }
  collectText(bag['cause'], depth + 1, seen, out);
}

function firstNumber(text: string, patterns: readonly RegExp[]): number | null {
  for (const pattern of patterns) {
    const hit = pattern.exec(text)?.[1];
    if (hit === undefined) continue;
    const n = Number(hit.replaceAll(',', ''));
    if (Number.isSafeInteger(n) && n > 0) return n;
  }
  return null;
}

/**
 * Parse a provider's context-overflow refusal into its stated numbers, or `null`
 * when `err` is not an overflow at all.
 *
 * "Is it an overflow" is {@link hasLlmContextOverflowMarker}'s answer, not a
 * second vocabulary — so the grader and this parser cannot disagree about which
 * errors are overflows. The numbers are RAW: plausibility is the WRITER's
 * (`recordOverflowLimits` runs `plausibleContextLength`), because a caller sizing
 * a retry and a store persisting a window may want different floors.
 */
export function parseContextOverflow(err: unknown): ContextOverflow | null {
  const parts: string[] = [];
  collectText(err, 0, new Set(), parts);
  const text = parts.join('\n');
  if (!hasLlmContextOverflowMarker(text)) return null;
  return {
    contextWindow: firstNumber(text, WINDOW_PATTERNS),
    inputTokens: firstNumber(text, INPUT_PATTERNS),
    requestedOutput: firstNumber(text, OUTPUT_PATTERNS),
  };
}

/**
 * The run-event name a box streams when a provider refused a call as over its
 * window (#4240). ONE name for every producer — the review lane and the harness
 * loop — so the mothership has one detector and one writer for the fact.
 */
export const CONTEXT_OVERFLOW_LEARNED_EVENT = 'context_overflow_learned' as const;

const tokenCount = z.number().int().positive().nullable();

/**
 * The `status` event payload carrying {@link CONTEXT_OVERFLOW_LEARNED_EVENT}: the
 * provider's stated numbers ({@link ContextOverflow}, snake_case on the wire) for
 * the model and key the refused call ran on.
 *
 * The parse is the TRUST BOUNDARY: a box is untrusted input, and `key_id` +
 * `context_window` steer a write to `byok_key_models`. The payload carries NO
 * account — the sink fences on the run's established owner, never on anything a
 * box says. `key_id` is null when the call ran on no pool key (nothing to learn
 * onto; the event still lands as evidence). Numbers are raw here; plausibility is
 * the writer's (`recordOverflowLimits`, `recordOutputCeilingRefusal`).
 */
export const ContextOverflowLearnedPayloadSchema = z.object({
  event: z.literal(CONTEXT_OVERFLOW_LEARNED_EVENT),
  provider: z.string().min(1),
  model: z.string().min(1),
  key_id: z.string().min(1).nullable(),
  context_window: tokenCount,
  input_tokens: tokenCount,
  requested_output: tokenCount,
  /**
   * The OUTPUT ceiling the provider named in a `[1, N]` refusal of `max_tokens`
   * (`refusedOutputCeiling`, `@developerz/agent`), steering an only-lowers write of
   * `byok_key_models.max_output_tokens` (`recordOutputCeilingRefusal`). Such an event
   * carries `context_window: null` — the refusal states no window. OPTIONAL on the wire:
   * the worker rolls before boxes, and a box built before the field sends none.
   */
  max_output_tokens: z.number().int().positive().optional(),
});
export type ContextOverflowLearnedPayload = z.infer<typeof ContextOverflowLearnedPayloadSchema>;
